import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, 
  Map, 
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  HelpCircle,
  Bell,
  Search,
  Brain,
  BookOpen,
  Sparkles,
  Target,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { HumaTwinIcon } from '../components/brand/HumaTwinLogo';

export default function PersonalLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Main navigation items matching the design reference
  const navItems = [
    { label: t('nav.myTwin'), path: '/personal/twin', icon: User },
    { label: t('nav.identity') || 'Identity', path: '/personal/identity', icon: Brain },
    { label: t('nav.knowledge') || 'Knowledge', path: '/personal/vault', icon: BookOpen },
    { label: t('nav.reflections') || 'Reflections', path: '/personal/reflections', icon: Sparkles },
    { label: t('nav.lifeMap'), path: '/personal/life-map', icon: Map },
    { label: t('nav.goals') || 'Goals', path: '/personal/goals', icon: Target },
    { label: t('nav.privacy'), path: '/personal/privacy', icon: Shield },
  ];

  // Bottom section items
  const bottomItems = [
    { label: t('nav.settings'), path: '/personal/settings', icon: Settings },
    { label: t('nav.support') || 'Support', path: '/personal/support', icon: HelpCircle },
  ];

  const isActive = (path) => {
    if (path === '/personal/twin') {
      return location.pathname === '/personal/twin' || 
             location.pathname === '/personal/dashboard' ||
             location.pathname.startsWith('/personal/assistant');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex bg-gray-50 personal-selection">
      {/* Sidebar - Desktop */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 flex flex-col ht-sidebar
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo Section */}
        <div className="p-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <HumaTwinIcon size={28} />
          </div>
          <span className="font-heading text-xl font-bold text-white">HumaTwin</span>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`ht-sidebar-item ${active ? 'active' : ''}`}
              >
                <Icon className="icon" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          {bottomItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`ht-sidebar-item ${active ? 'active' : ''}`}
              >
                <Icon className="icon" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Bar */}
        <header className="ht-topbar px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>

          {/* Search Bar - Hidden on mobile */}
          <div className="hidden md:flex flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search') || 'Search...'}
                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:bg-white transition-all"
                data-testid="global-search"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Search Icon - Mobile only */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden p-2"
              onClick={() => setSearchOpen(!searchOpen)}
            >
              <Search className="w-5 h-5 text-gray-600" />
            </Button>

            {/* Notifications */}
            <Button
              variant="ghost"
              size="sm"
              className="p-2 relative"
              data-testid="notifications-button"
            >
              <Bell className="w-5 h-5 text-gray-600" />
            </Button>

            {/* Language Switcher */}
            <div className="hidden sm:block">
              <LanguageSwitcher variant="compact" />
            </div>

            {/* User Avatar */}
            <div className="flex items-center gap-2">
              <Avatar className="w-9 h-9 border-2 border-gray-200">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-primary text-white text-sm font-medium">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              
              {/* User dropdown - Desktop only */}
              <div className="hidden lg:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-gray-600 hover:text-gray-900"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Search - Expandable */}
        {searchOpen && (
          <div className="md:hidden px-4 pb-3 bg-white border-b border-gray-200 animate-fade-in">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('common.search') || 'Search...'}
                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
